/**
 * Charity Verify — admin-only screen.
 *
 * Donation-mode apps with a populated `causeProfile.taxIdNumber` (EIN)
 * land in the Pending queue. Staff manually checks the IRS Tax-Exempt
 * Organization Search lookup tool, then clicks Verify (with optional
 * notes) or Unverify (with a required reason). Both actions write
 * audit-log entries via the API.
 *
 * Mirrors the legacy CharityVerify view on app.nurev.io. Two
 * structural adaptations to admin-console conventions:
 *   - toasts → in-modal inline error + auto-close on success (the
 *     admin console has no react-toastify dependency; this matches the
 *     Escalations / Crypto Assets mutation pattern).
 *   - bespoke StatCard → shared atoms StatCard.
 *
 * Tabs: Pending (default) and Verified (for review / revocation).
 */
import { useEffect, useState } from 'react';
import { ExternalLink, Check } from 'lucide-react';
import { CharityRow } from '@/api/charityVerify';
import {
  useCharityQueue,
  useCharityVerified,
  useVerifyCharity,
  useUnverifyCharity
} from '@/hooks/useCharityVerify';
import { ActionButton, Card, Skeleton, StatCard } from '@/components/ui/atoms';
import { fmtDate } from '@/lib/format';

type Tab = 'pending' | 'verified';

const irsLookupUrl = (ein?: string | null) =>
  ein
    ? `https://apps.irs.gov/app/eos/allSearch?ein1=${encodeURIComponent(
        ein
      )}&names=&city=&state=All&country=US&deductibility=all`
    : 'https://apps.irs.gov/app/eos/allSearch';

// ─── Charity row ──────────────────────────────────────────────────────────

function CharityRowCard({
  row,
  mode,
  onVerify,
  onUnverify
}: {
  row: CharityRow;
  mode: Tab;
  onVerify?: (r: CharityRow) => void;
  onUnverify?: (r: CharityRow) => void;
}) {
  const verified = !!row.causeProfile?.isVerifiedCharity;
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h4 className="truncate text-lg font-bold text-nurev-text">
              {row.appName || row.appKey}
            </h4>
            {verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-500/30">
                <Check className="h-3 w-3" /> 501(c)(3) Verified
              </span>
            )}
          </div>
          <p className="text-sm text-nurev-muted">
            Publisher:{' '}
            <span className="text-nurev-text">{row.publisherName || '—'}</span>{' '}
            {row.publisherEmail && (
              <span className="text-nurev-muted">({row.publisherEmail})</span>
            )}
          </p>
          {row.causeProfile?.mission && (
            <p className="mt-2 text-sm italic text-nurev-text-soft">
              "{row.causeProfile.mission}"
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-nurev-muted">
            <span>
              EIN:{' '}
              <span className="font-mono text-nurev-text">
                {row.causeProfile?.taxIdNumber || '—'}
              </span>
            </span>
            <span>
              Installs:{' '}
              <span className="text-nurev-text">
                {(row.installs || 0).toLocaleString()}
              </span>
            </span>
            <span>
              Created:{' '}
              <span className="text-nurev-text">{fmtDate(row.createdAt)}</span>
            </span>
          </div>
          {(row.causeProfile?.websiteUrl || row.causeProfile?.taxIdNumber) && (
            <div className="mt-3 flex flex-wrap gap-3">
              {row.causeProfile?.websiteUrl && (
                <a
                  href={row.causeProfile.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"
                >
                  Website
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {row.causeProfile?.taxIdNumber && (
                <a
                  href={irsLookupUrl(row.causeProfile.taxIdNumber)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"
                >
                  IRS Tax-Exempt Lookup
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="flex min-w-[140px] shrink-0 flex-col gap-2">
          {mode === 'pending' && (
            <ActionButton variant="positive" onClick={() => onVerify?.(row)}>
              ✓ Verify
            </ActionButton>
          )}
          {mode === 'verified' && (
            <ActionButton
              variant="destructive"
              onClick={() => onUnverify?.(row)}
            >
              Unverify
            </ActionButton>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Verify modal ─────────────────────────────────────────────────────────

function VerifyModal({
  target,
  onClose
}: {
  target: CharityRow;
  onClose: () => void;
}) {
  const verify = useVerifyCharity();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await verify.mutateAsync({ appId: target.appId, notes: notes.trim() });
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Verification failed';
      setError(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-bold text-nurev-text">
          Verify {target.appName || target.appKey}
        </h3>
        <p className="mb-4 text-sm text-nurev-muted">
          Confirm the EIN{' '}
          <span className="font-mono text-nurev-text">
            {target.causeProfile?.taxIdNumber}
          </span>{' '}
          matches the IRS lookup. This will set the 501(c)(3) Verified badge on
          this app's donor receipts.
        </p>
        <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
          Verification Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Matched IRS lookup 2026-04-29, EIN active, public charity"
          maxLength={500}
          rows={3}
          className="mb-4 w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none"
        />
        {error && <p className="mb-3 text-xs text-red-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <ActionButton variant="neutral" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="positive"
            disabled={verify.isPending}
            onClick={handleSubmit}
          >
            {verify.isPending ? 'Verifying…' : '✓ Confirm Verify'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ─── Unverify modal ───────────────────────────────────────────────────────

function UnverifyModal({
  target,
  onClose
}: {
  target: CharityRow;
  onClose: () => void;
}) {
  const unverify = useUnverifyCharity();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    setError(null);
    try {
      await unverify.mutateAsync({
        appId: target.appId,
        reason: reason.trim()
      });
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Unverify failed';
      setError(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-bold text-nurev-text">
          Unverify {target.appName || target.appKey}
        </h3>
        <p className="mb-4 text-sm text-nurev-muted">
          This will remove the 501(c)(3) Verified badge. A reason is required
          for the audit log.
        </p>
        <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
          Reason (required)
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. EIN no longer active per IRS lookup, publisher request"
          maxLength={500}
          rows={3}
          required
          className="mb-4 w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-red-500/50 focus:outline-none"
        />
        {error && <p className="mb-3 text-xs text-red-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <ActionButton variant="neutral" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="destructive"
            disabled={unverify.isPending}
            onClick={handleSubmit}
          >
            {unverify.isPending ? 'Unverifying…' : 'Confirm Unverify'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function CharityVerifyScreen() {
  const [tab, setTab] = useState<Tab>('pending');
  const [verifyTarget, setVerifyTarget] = useState<CharityRow | null>(null);
  const [unverifyTarget, setUnverifyTarget] = useState<CharityRow | null>(null);

  const { data: queueData, isLoading: queueLoading } = useCharityQueue({
    enabled: tab === 'pending'
  });
  const { data: verifiedData, isLoading: verifiedLoading } = useCharityVerified({
    enabled: tab === 'verified'
  });

  const queueRows = queueData?.docs || [];
  const verifiedRows = verifiedData?.docs || [];

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'pending', label: `Pending (${queueRows.length})` },
    { key: 'verified', label: `Verified (${verifiedRows.length})` }
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Charity Verify
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Manually verify donation-mode apps claiming 501(c)(3) status.
            Cross-check each EIN against the IRS Tax-Exempt Organization Search
            before flipping the badge.
          </p>
        </div>
        <a
          href="https://app.nurev.io/charity-verify"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          label="Pending Verification"
          value={queueRows.length.toLocaleString()}
          sub="Apps awaiting review"
          loading={queueLoading}
          color="text-amber-300"
        />
        <StatCard
          label="Verified Charities"
          value={verifiedRows.length.toLocaleString()}
          sub="Active 501(c)(3) badges"
          loading={verifiedLoading}
          color="text-emerald-300"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-nurev-border">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-amber-500 text-nurev-text'
                : 'border-transparent text-nurev-muted hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Lists */}
      {tab === 'pending' && (
        <div className="space-y-3">
          {queueLoading && <Skeleton className="h-32 w-full" />}
          {!queueLoading && queueRows.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-nurev-muted">
                No charities awaiting verification. 🎉
              </p>
            </Card>
          )}
          {queueRows.map(row => (
            <CharityRowCard
              key={row.appId}
              row={row}
              mode="pending"
              onVerify={setVerifyTarget}
            />
          ))}
        </div>
      )}

      {tab === 'verified' && (
        <div className="space-y-3">
          {verifiedLoading && <Skeleton className="h-32 w-full" />}
          {!verifiedLoading && verifiedRows.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-nurev-muted">No verified charities yet.</p>
            </Card>
          )}
          {verifiedRows.map(row => (
            <CharityRowCard
              key={row.appId}
              row={row}
              mode="verified"
              onUnverify={setUnverifyTarget}
            />
          ))}
        </div>
      )}

      {verifyTarget && (
        <VerifyModal
          target={verifyTarget}
          onClose={() => setVerifyTarget(null)}
        />
      )}
      {unverifyTarget && (
        <UnverifyModal
          target={unverifyTarget}
          onClose={() => setUnverifyTarget(null)}
        />
      )}
    </div>
  );
}
